import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, ClipboardCheck, PackageCheck, Truck, Unlink } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';
import type { Location } from '../types';
import { ClientBlankSupplyMirrorLine } from './ClientBlankSupplyGrant';

interface Props {
  locations: Location[];
  /**
   * BLU-367 claim-shadow — the org's own (non-shared) IMS locations. Used to
   * detect when an owned location's shopify_location_id shadows one of these
   * shared connections in order routing (getLocationUuid checks owned
   * locations before the 3PL junction).
   */
  ownLocations?: Location[];
  /**
   * Opens the existing DelegateFulfilmentDialog (rendered by the parent) for
   * `ownLocation`, pre-selecting `sharedLocationId` as the delegation target.
   */
  onDelegate?: (ownLocation: Location, sharedLocationId: string) => void;
}

/**
 * Returns the org's own location shadowing this shared connection, if any —
 * an owned location whose shopify_location_id matches the junction's chosen
 * client_shopify_location_id, but which isn't ALREADY delegated to this
 * specific shared location.
 */
function findShadowingOwnLocation(shared: Location, ownLocations: Location[]): Location | null {
  if (shared.client_shopify_location_id == null) return null;
  return (
    ownLocations.find(
      (own) =>
        own.shopify_location_id === shared.client_shopify_location_id &&
        own.delegated_to_location_id !== shared.id,
    ) ?? null
  );
}

interface DisconnectTarget {
  locationId: string;
  locationName: string;
  ownerName: string;
}

/**
 * BLU-367 Phase E.13 — client-side post-claim location card section.
 *
 * Renders locations the caller's org reaches via an active 3PL junction
 * (`is_shared === true`). Each card carries the §13.3 purple-ribbon
 * treatment so it visually differentiates from owned locations without
 * tinting the whole card surface (anti-pattern per DESIGN.md §13).
 *
 * Disconnect uses the same revokeClientAccess mutation as the owner side,
 * but the service-level isOwner-OR-isClient gate allows the client to
 * revoke their OWN junction.
 */
export default function ConnectedVia3PLSection({ locations, ownLocations = [], onDelegate }: Props) {
  const [pendingDisconnect, setPendingDisconnect] = useState<DisconnectTarget | null>(null);
  const utils = trpc.useUtils();
  const { organization } = useOrganization();

  const revokeMutation = trpc.locations.revokeClientAccess.useMutation({
    onSuccess: () => {
      toast({ title: 'Disconnected', description: 'Orders will stop routing to this warehouse.' });
      void utils.locations.list.invalidate();
      setPendingDisconnect(null);
    },
    onError: (err) => {
      toast({
        title: 'Could not disconnect',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  return (
    // BLU-389 review: header + description live in the parent §3 surface heading
    // (LocationManagement.tsx). This card just renders the row list — keeps a
    // single "3PL Connections" title on the page instead of stacking two.
    <Card>
      <CardContent className="space-y-3 pt-6">
        {locations.map((loc) => {
          const ownerName = loc.owner_organization_name?.trim() || '3PL partner';
          const shadowingOwnLocation = findShadowingOwnLocation(loc, ownLocations);
          return (
            <div
              key={loc.id}
              className="rounded-lg border border-l-4 border-l-threadheads-purple/30 bg-card p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                    {ownerName}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{loc.name}</span>
                    <Badge className="bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300/80 text-[10px] uppercase tracking-wide">
                      Active
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {loc.city ? `${loc.city} · ` : ''}
                    Bound to your Shopify location ID{' '}
                    <span className="font-mono">{loc.shopify_location_id}</span>
                  </div>
                  {/* BLU-367 C2 write-back — read-only indicator. The 3PL owner
                      controls the toggle (SharedLocationsCard); the client just
                      sees that it's on. Muted help-text idiom mirrors the owner
                      side's "read at a glance" treatment. */}
                  {loc.fulfillment_writeback_enabled === true && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
                      <PackageCheck
                        className="mt-px h-3.5 w-3.5 flex-shrink-0 text-emerald-600"
                        aria-hidden="true"
                      />
                      <span>
                        {ownerName} can mark your Shopify orders fulfilled for manual shipments.
                      </span>
                    </div>
                  )}
                  {/* BLU-711 A1 — read-only mirror of the owner's stock-write
                      grant, same idiom as the write-back indicator above. Without
                      it the capability appears silently: the Inventory toolbar
                      just becomes clickable with nothing saying why. */}
                  {loc.client_stock_writes_enabled === true && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
                      <ClipboardCheck
                        className="mt-px h-3.5 w-3.5 flex-shrink-0 text-emerald-600"
                        aria-hidden="true"
                      />
                      <span>You can adjust your own stock at this warehouse.</span>
                    </div>
                  )}
                  {/* Phase 92 (D-08, UI-SPEC 2.4): read-only mirror of the
                      owner's "use my blanks" consent, with where to start.
                      Translated; see ClientBlankSupplyMirrorLine. */}
                  <ClientBlankSupplyMirrorLine
                    enabled={loc.client_blank_supply_enabled === true}
                    ownerName={ownerName}
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-700"
                  onClick={() =>
                    setPendingDisconnect({
                      locationId: loc.id,
                      locationName: loc.name,
                      ownerName,
                    })
                  }
                  disabled={revokeMutation.isPending}
                >
                  <Unlink className="mr-1.5 h-3.5 w-3.5" />
                  Disconnect
                </Button>
              </div>

              {shadowingOwnLocation && (
                <Alert variant="warning" className="mt-3">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span>
                      Orders for &ldquo;{shadowingOwnLocation.name}&rdquo; aren&rsquo;t routing here
                      yet.
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-threadheads-purple/40 text-threadheads-purple hover:bg-threadheads-purple/5 hover:text-threadheads-purple"
                      onClick={() => onDelegate?.(shadowingOwnLocation, loc.id)}
                    >
                      <Truck className="mr-1.5 h-3.5 w-3.5" />
                      Delegate fulfilment
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          );
        })}
      </CardContent>

      <AlertDialog
        open={pendingDisconnect !== null}
        onOpenChange={(next) => !next && setPendingDisconnect(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disconnect from {pendingDisconnect?.ownerName ?? 'this 3PL partner'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Your orders will stop routing to {pendingDisconnect?.ownerName ?? 'this partner'}&rsquo;s {pendingDisconnect?.locationName ?? 'warehouse'}. Orders already in progress there will finish normally. You can reconnect later if {pendingDisconnect?.ownerName ?? 'they'} shares a new claim code.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-700 bg-transparent"
              disabled={revokeMutation.isPending}
              onClick={() => {
                if (!pendingDisconnect) return;
                if (!organization?.id) {
                  toast({
                    title: 'Could not disconnect',
                    description: 'Organisation context not loaded yet, try again in a moment.',
                    variant: 'destructive',
                  });
                  return;
                }
                // BLU-367: client-side revoke. The backend service checks
                // ctx.organizationId === input.clientOrganizationId on the
                // client-side path, so we pass our active org id from the
                // OrganizationContext. The JWT-derived ctx.organizationId
                // is the source of truth at the service layer.
                revokeMutation.mutate({
                  locationId: pendingDisconnect.locationId,
                  clientOrganizationId: organization.id,
                  reason: 'revoked_by_client',
                });
              }}
            >
              {revokeMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
